import React, { useState, useRef, useEffect } from 'react';
import './SignupLoginModal.scss';
import './EmailVerification.scss';
import { Trans, useTranslation } from 'react-i18next';
import { getCookie } from '../utils/utils';

/**
 * Inputs of the verification-code screen: the address the code was sent to,
 * the controlled code value, and the submit/loading/error state owned by the
 * modal shell.
 */
type Props = {
  email: string;
  verificationCode: string;
  setVerificationCode: (code: string) => void;
  onSubmitCode: () => void;
  isVerifying: boolean;
  verificationError: string;
  onTitleChange?: (title: string) => void;
  /** Send a fresh code once when the step opens (reached from a login of an unconfirmed account). */
  resendOnOpen?: boolean;
};

/**
 * Body of the "check your email" step: the code field, its submit button and
 * the resend control. Renders the screen body only; the sheet, padding and
 * header row belong to the modal shell.
 */
const EmailVerification = (props: Props) => {
  const {
    email,
    verificationCode,
    setVerificationCode,
    onSubmitCode,
    isVerifying,
    verificationError,
    resendOnOpen,
  } = props;
  const { t, i18n } = useTranslation();

  // Refs
  const verificationCodeRef = useRef<HTMLInputElement>(null);
  const phoneInputRef = useRef<HTMLInputElement>(null);

  // SMS fallback state
  const [showSmsOption, setShowSmsOption] = useState(false);
  const [showPhoneInput, setShowPhoneInput] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('+358');
  const [phoneError, setPhoneError] = useState('');
  const [smsSentSuccess, setSmsSentSuccess] = useState(false);
  const [smsSentToNumber, setSmsSentToNumber] = useState('');
  const [isSendingSms, setIsSendingSms] = useState(false);
  const [smsError, setSmsError] = useState('');

  // Resend email state
  const [isResendingEmail, setIsResendingEmail] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState('');

  // Focus the code input on mount
  useEffect(() => {
    if (verificationCodeRef.current) {
      verificationCodeRef.current.focus();
    }
  }, []);

  // Reveal SMS option when there's a verification error
  useEffect(() => {
    if (verificationError) {
      setShowSmsOption(true);
    }
  }, [verificationError]);

  // Focus phone input when it appears
  useEffect(() => {
    if (showPhoneInput && phoneInputRef.current) {
      phoneInputRef.current.focus();
    }
  }, [showPhoneInput]);

  // Re-focus code input after SMS is sent
  useEffect(() => {
    if (smsSentSuccess && verificationCodeRef.current) {
      verificationCodeRef.current.focus();
    }
  }, [smsSentSuccess]);

  const validateFinnishPhone = (phone: string): boolean => {
    return /^\+358\d{6,12}$/.test(phone.replace(/\s/g, ''));
  };

  const handleResendEmail = async () => {
    setIsResendingEmail(true);
    setResendSuccess(false);
    setResendError('');

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/signup/resend-verification-email`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': getCookie('csrf_access_token'),
          },
          body: JSON.stringify({
            email: email.toLowerCase(),
            language: i18n.language,
          }),
          credentials: 'include',
        },
      );

      if (response.ok) {
        setResendSuccess(true);
        setShowSmsOption(true);
        // Clear success message after 5 seconds
        setTimeout(() => setResendSuccess(false), 5000);
      } else {
        const errorData = await response.json();
        if (errorData.error === 'rate_limit_exceeded') {
          setResendError(
            t('signupLoginModal.emailVerification.rateLimitError'),
          );
        } else {
          setResendError(t('signupLoginModal.unexpectedError'));
        }
      }
    } catch (error) {
      setResendError(t('signupLoginModal.unexpectedError'));
    } finally {
      setIsResendingEmail(false);
    }
  };

  /** Send a fresh code once on open when this step was reached from a login of an unconfirmed account. */
  useEffect(() => {
    if (resendOnOpen) handleResendEmail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Relays the email code over SMS; a 403 means this browser holds no pending_signup cookie.
  const handleSendSms = async () => {
    const cleanPhone = phoneNumber.replace(/\s/g, '');

    if (!validateFinnishPhone(cleanPhone)) {
      setPhoneError(t('signupLoginModal.emailVerification.invalidPhone'));
      return;
    }

    setIsSendingSms(true);
    setSmsError('');
    setPhoneError('');

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/signup/request-sms-verification`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': getCookie('csrf_access_token'),
          },
          body: JSON.stringify({
            email: email.toLowerCase(),
            phone_number: cleanPhone,
          }),
          credentials: 'include',
        },
      );

      if (response.ok) {
        setSmsSentSuccess(true);
        setSmsSentToNumber(cleanPhone);
        setShowPhoneInput(false);
        setVerificationCode('');
      } else {
        const errorData = await response.json();
        if (errorData.error === 'invalid_phone_number') {
          setPhoneError(t('signupLoginModal.emailVerification.invalidPhone'));
        } else if (errorData.error === 'signup_session_required') {
          setSmsError(t('signupLoginModal.emailVerification.sessionRequired'));
        } else {
          setSmsError(t('signupLoginModal.emailVerification.smsError'));
        }
      }
    } catch (error) {
      setSmsError(t('signupLoginModal.emailVerification.smsError'));
    } finally {
      setIsSendingSms(false);
    }
  };

  // Dynamic title and subtitle based on SMS mode
  const title = smsSentSuccess
    ? t('signupLoginModal.emailVerification.smsTitle')
    : t('signupLoginModal.emailVerification.title');

  // The shell draws the title in the header row, and only this component knows
  // which of the two wordings applies.
  useEffect(() => {
    props.onTitleChange?.(title);
  }, [title, props]);

  const subtitle = smsSentSuccess
    ? t('signupLoginModal.emailVerification.smsSubtitle', {
        phone: smsSentToNumber,
      })
    : undefined;

  return (
    <div className="email-verification">
      <div className="email-verification__intro">
        <h2 className="email-verification__heading">{title}</h2>

        <div className="email-verification__helper">
          {smsSentSuccess ? (
            <div>{subtitle}</div>
          ) : (
            <>
              <div>
                <Trans i18nKey="signupLoginModal.emailVerification.firstParagraph" />
              </div>
              <div>
                <Trans
                  i18nKey="signupLoginModal.emailVerification.secondParagraph"
                  values={{ email }}
                />
              </div>
            </>
          )}
        </div>

        {/* SMS sent success message */}
        {smsSentSuccess && (
          <div className="email-verification__notice">
            <Trans
              i18nKey="signupLoginModal.emailVerification.smsSentSuccess"
              values={{ phone: smsSentToNumber }}
            />
          </div>
        )}

        {/* Resend success message */}
        {resendSuccess && !smsSentSuccess && (
          <div className="email-verification__notice">
            <Trans i18nKey="signupLoginModal.emailVerification.resendSuccess" />
          </div>
        )}
      </div>

      {/* Verification code form */}
      <form
        className="email-verification__form"
        onSubmit={(e) => {
          e.preventDefault();
          onSubmitCode();
        }}
      >
        <div className="email-verification__field">
          <input
            type="text"
            placeholder={t(
              'signupLoginModal.emailVerification.codePlaceholder',
            )}
            className={`email-verification__code${
              verificationError ? ' email-verification__code--invalid' : ''
            }`}
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            required
            ref={verificationCodeRef}
            inputMode="numeric"
            autoComplete="one-time-code"
          />

          {verificationError && (
            <div className="email-verification__error">{verificationError}</div>
          )}
        </div>

        <button
          className={`email-verification__submit${
            isVerifying ? ' email-verification__submit--loading' : ''
          }`}
          disabled={isVerifying}
          type="submit"
        >
          {isVerifying ? (
            <span className="loading-dots">
              <span></span>
            </span>
          ) : (
            <Trans i18nKey="signupLoginModal.emailVerification.submitButton" />
          )}
        </button>
      </form>

      <div className="email-verification__actions">
        {/* Resend code link */}
        {!smsSentSuccess && (
          <button
            className="email-verification__resend"
            onClick={handleResendEmail}
            disabled={isResendingEmail}
            type="button"
          >
            {isResendingEmail ? (
              <span className="email-verification__resend-loading">...</span>
            ) : (
              <Trans i18nKey="signupLoginModal.emailVerification.resendCode" />
            )}
          </button>
        )}

        {/* Resend error */}
        {resendError && (
          <div className="email-verification__error">{resendError}</div>
        )}
      </div>

      {/* SMS fallback link (revealed after error or resend) */}
      {/* {showSmsOption && !showPhoneInput && !smsSentSuccess && (
        <button
          className="signup-login-modal__sms-fallback-button"
          onClick={() => setShowPhoneInput(true)}
          type="button"
        >
          <Trans i18nKey="signupLoginModal.emailVerification.smsVerifyLink" />
        </button>
      )} */}

      {/* Phone input section (slides in) */}
      {/*       <div className={`signup-login-modal__phone-section ${showPhoneInput ? 'signup-login-modal__phone-section--visible' : ''}`}>
        <div className="signup-login-modal__phone-section__inner">
          <input
            type="tel"
            placeholder={t('signupLoginModal.emailVerification.phonePlaceholder')}
            className="signup-login-modal__content__input"
            value={phoneNumber}
            onChange={(e) => {
              setPhoneNumber(e.target.value);
              setPhoneError('');
              setSmsError('');
            }}
            ref={phoneInputRef}
          />

          {phoneError && (
            <div className="signup-login-modal__content__error">
              {phoneError}
            </div>
          )}

          {smsError && (
            <div className="signup-login-modal__content__error">
              {smsError}
            </div>
          )}

          <button
            className="signup-login-modal__content__button signup-login-modal__content__button--sms"
            onClick={handleSendSms}
            disabled={isSendingSms}
            type="button"
          >
            {isSendingSms ? (
              <span className="loading-dots">
                <span></span>
              </span>
            ) : (
              <Trans i18nKey="signupLoginModal.emailVerification.sendSmsButton" />
            )}
          </button>
        </div>
      </div> */}
    </div>
  );
};

export default EmailVerification;
